import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Menu, X, ChevronDown, User, LogOut, Settings, LayoutDashboard, Scale, Search, FileText, CreditCard, Upload } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useNavigate } from 'react-router';
import clsx from 'clsx';
import { useAuthContext } from '../../contexts/AuthContext';
import { AuthModal } from './AuthModal';
import { FAIRPATH_PROCESS_STEPS } from '../../lib/marketing/processContent';
import { useAppNavigate } from '../hooks/useAppNavigate';

type AuthMode = "signup" | "login" | "forgot";

interface MarketingNavbarProps {
  activePage?: string;
  onNavigate?: (page: string) => void;
  showAddressCta?: boolean;
}

const NAV_LINKS = [
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'sample-packet', label: 'Sample Packet', href: '/packet/sample' },
  // Restored 2026-08-18. #934 swapped Coverage out for Sample Packet, but
  // /coverage stayed routed, MarketingLayout kept mapping it to an active-nav
  // state that no longer existed, and the state and county pages kept linking
  // to it — so the top nav was the one place a visitor could not find the page
  // that answers "do you work where I live".
  { id: 'coverage', label: 'Coverage', href: '/coverage' },
  { id: 'tax-comparison', label: 'Tax Comparison', href: '/property-taxes-by-state' },
  { id: 'blog', label: 'Blog' },
];

const PROCESS_ICONS = [Search, FileText, Upload] as const;

// Every customer-facing process summary uses the same canonical three stages.
const HOW_IT_WORKS_STEPS = FAIRPATH_PROCESS_STEPS.map((step, index) => ({
  step: index + 1,
  title: step.title,
  description: step.description,
  icon: PROCESS_ICONS[index],
}));

type NavViewport = "mobile" | "tablet" | "desktop";

function getNavViewport(): NavViewport {
  if (typeof window === "undefined") return "desktop";
  if (window.innerWidth >= 1280) return "desktop";
  if (window.innerWidth >= 1024) return "tablet";
  return "mobile";
}

export function MarketingNavbar({ activePage = 'home', onNavigate, showAddressCta = true }: MarketingNavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isHowItWorksMobileOpen, setIsHowItWorksMobileOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthMode>("signup");
  const [navViewport, setNavViewport] = useState<NavViewport>(() => getNavViewport());
  const userMenuRef = useRef<HTMLDivElement>(null);
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const howItWorksTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { appNavigate } = useAppNavigate();
  const { isAuthenticated, user, profile, signOut } = useAuthContext();

  /** Open the AuthModal in the given mode from any nav breakpoint. */
  const openAuth = (mode: AuthMode) => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setNavViewport(getNavViewport());
      setIsHowItWorksOpen(false);
      setIsMobileMenuOpen(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close user menu and HIW dropdown on outside click.
  // Uses 'click' (not 'mousedown') so menu item onClick handlers fire first —
  // mousedown closes the menu before the click event reaches the button.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (howItWorksRef.current && !howItWorksRef.current.contains(e.target as Node)) {
        setIsHowItWorksOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setIsUserMenuOpen(false);
      setIsHowItWorksOpen(false);
      setIsMobileMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cleanup hover timers on unmount
  useEffect(() => {
    return () => {
      if (howItWorksTimerRef.current) clearTimeout(howItWorksTimerRef.current);
    };
  }, []);

  const handleHowItWorksEnter = () => {
    if (howItWorksTimerRef.current) clearTimeout(howItWorksTimerRef.current);
    setIsHowItWorksOpen(true);
  };

  const handleHowItWorksLeave = () => {
    // Small delay so user can move to dropdown panel without it closing
    howItWorksTimerRef.current = setTimeout(() => setIsHowItWorksOpen(false), 150);
  };

  const handleNavClick = (pageId: string, href?: string) => {
    if (href) {
      navigate(href);
    } else {
      (onNavigate ?? appNavigate)(pageId);
    }
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    await signOut();
    navigate('/');
  };

  const displayName =
    profile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Account';

  const displayEmail = user?.email || '';

  // Get initials for avatar
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <nav
        aria-label="Primary"
        className={clsx(
          "sticky top-0 z-50 w-full bg-fp-slate-100 border-b transition-colors duration-150",
          isScrolled ? "border-fp-slate-300" : "border-transparent",
          isScrolled ? "shadow-[0_1px_3px_rgba(15,23,42,0.08)]" : ""
        )}
        style={{ height: 'auto' }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16 lg:h-16">

            {/* Logo */}
            <button
              type="button"
              className="flex-shrink-0 cursor-pointer"
              onClick={() => handleNavClick('home')}
              aria-label="FairPath home"
            >
              <span className="font-inter font-bold text-[24px] text-fp-navy-900 tracking-[-0.03em]">
                FairPath.
              </span>
            </button>

            {/* Desktop Navigation */}
            {navViewport === "desktop" && (
            <div className="flex items-center gap-6">
              {NAV_LINKS.map((link) => {
                const isActive = activePage === link.id;

                // Direct-route links (e.g. CRE Pro → /commercial)
                if ('href' in link && link.href) {
                  return (
                    <Link
                      key={link.id}
                      to={link.href}
                      aria-current={activePage === link.id ? "page" : undefined}
                      className={clsx(
                        "relative font-inter font-medium text-[16px] transition-colors duration-150 py-5",
                        isActive ? "text-fp-emerald-600" : "text-fp-navy-900 hover:text-fp-slate-600"
                      )}
                    >
                      {link.label}
                      {isActive && (
                        <span className="absolute bottom-0 left-0 w-full h-[2px] bg-fp-emerald-500" />
                      )}
                    </Link>
                  );
                }

                // "How It Works" gets a hover dropdown
                if (link.id === 'how-it-works') {
                  return (
                    <div
                      key={link.id}
                      ref={howItWorksRef}
                      className="relative"
                      onMouseEnter={handleHowItWorksEnter}
                      onMouseLeave={handleHowItWorksLeave}
                    >
                      <button
                        onClick={() => handleNavClick(link.id)}
                        aria-expanded={isHowItWorksOpen}
                        aria-controls="fairpath-how-it-works-menu"
                        aria-haspopup="true"
                        className={clsx(
                          "relative font-inter font-medium text-[16px] transition-colors duration-150 py-5 flex items-center gap-1",
                          isActive ? "text-fp-emerald-600" : "text-fp-navy-900 hover:text-fp-slate-600"
                        )}
                      >
                        {link.label}
                        <ChevronDown className={clsx(
                          "w-3.5 h-3.5 transition-transform duration-200",
                          isHowItWorksOpen && "rotate-180"
                        )} />
                        {isActive && (
                          <span className="absolute bottom-0 left-0 w-full h-[2px] bg-fp-emerald-500" />
                        )}
                      </button>

                      <AnimatePresence>
                        {isHowItWorksOpen && (
                          <motion.div
                            id="fairpath-how-it-works-menu"
                            role="region"
                            aria-label="How FairPath works"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.18 }}
                            className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-[380px] bg-fp-slate-100 border border-fp-slate-300 shadow-lg z-50 overflow-hidden"
                          >
                            <div className="p-5 space-y-4">
                              {HOW_IT_WORKS_STEPS.map(({ step, title, description, icon: Icon }) => (
                                <div key={step} className="flex items-start gap-3.5">
                                  <div className="flex items-center justify-center w-9 h-9 border border-fp-emerald-500/30 bg-fp-emerald-500/10 shrink-0">
                                    <Icon className="w-[18px] h-[18px] text-fp-emerald-500" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-inter font-semibold text-[14px] text-fp-navy-900 leading-snug">
                                      Step {step}: {title}
                                    </p>
                                    <p className="font-inter text-[13px] text-fp-slate-500 leading-relaxed mt-0.5">
                                      {description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-fp-slate-100 px-5 py-3.5">
                              <button
                                onClick={() => {
                                  setIsHowItWorksOpen(false);
                                  handleNavClick('home');
                                  // Scroll to address input after navigation
                                  setTimeout(() => {
                                    document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' });
                                  }, 100);
                                }}
                                className="w-full bg-fp-teal-700 hover:bg-fp-navy-900 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fp-teal-700 font-inter font-semibold text-[14px] h-10 transition-colors"
                              >
                                Get Started — Enter Your Address
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <button
                    key={link.id}
                    aria-current={activePage === link.id ? "page" : undefined}
                    onClick={() => handleNavClick(link.id)}
                    className={clsx(
                      "relative font-inter font-medium text-[16px] transition-colors duration-150 py-5",
                      isActive ? "text-fp-emerald-600" : "text-fp-navy-900 hover:text-fp-slate-600"
                    )}
                  >
                    {link.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 w-full h-[2px] bg-fp-emerald-500" />
                    )}
                  </button>
                );
              })}

              {isAuthenticated ? (
                /* ── Logged-in: User menu ── */
                <div className="relative ml-2" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 h-10 rounded-lg border border-fp-slate-200 hover:border-fp-slate-300 hover:bg-fp-slate-50 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-fp-emerald-500 flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">{initials}</span>
                    </div>
                    <span className="font-inter font-medium text-[14px] text-fp-navy-900 max-w-[120px] truncate">
                      {displayName}
                    </span>
                    <ChevronDown className={clsx(
                      "w-4 h-4 text-fp-slate-500 transition-transform",
                      isUserMenuOpen && "rotate-180"
                    )} />
                  </button>

                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-fp-slate-200 shadow-lg py-1 z-50"
                      >
                        {/* User info header */}
                        <div className="px-4 py-3 border-b border-fp-slate-100">
                          <p className="font-inter font-semibold text-[14px] text-fp-navy-900 truncate">
                            {displayName}
                          </p>
                          <p className="font-inter text-[12px] text-fp-slate-500 truncate">
                            {displayEmail}
                          </p>
                        </div>

                        <div className="py-1">
                          <button
                            onClick={() => { setIsUserMenuOpen(false); navigate('/appeal-hq'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-fp-slate-50 transition-colors"
                          >
                            <Scale className="w-4 h-4 text-fp-slate-500" />
                            <span className="font-inter text-[14px] text-fp-navy-900">My Appeals</span>
                          </button>
                          <button
                            onClick={() => { setIsUserMenuOpen(false); navigate('/appeal-hq'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-fp-slate-50 transition-colors"
                          >
                            <LayoutDashboard className="w-4 h-4 text-fp-slate-500" />
                            <span className="font-inter text-[14px] text-fp-navy-900">My Dashboard</span>
                          </button>
                          <button
                            onClick={() => { setIsUserMenuOpen(false); navigate('/account/settings'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-fp-slate-50 transition-colors"
                          >
                            <Settings className="w-4 h-4 text-fp-slate-500" />
                            <span className="font-inter text-[14px] text-fp-navy-900">Account Settings</span>
                          </button>
                          <button
                            onClick={() => { setIsUserMenuOpen(false); navigate('/account/billing'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-fp-slate-50 transition-colors"
                          >
                            <CreditCard className="w-4 h-4 text-fp-slate-500" />
                            <span className="font-inter text-[14px] text-fp-navy-900">Billing</span>
                          </button>
                        </div>

                        <div className="border-t border-fp-slate-100 py-1">
                          <button
                            onClick={handleSignOut}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-fp-slate-50 transition-colors"
                          >
                            <LogOut className="w-4 h-4 text-fp-red-600" />
                            <span className="font-inter text-[14px] text-fp-red-600">Sign Out</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                /* ── Logged-out: Sign In (subtle) + Check My Property CTA ── */
                <div className="flex items-center gap-2 ml-2">
                  <button
                    onClick={() => openAuth('login')}
                    className="font-inter text-[13px] text-fp-slate-500 hover:text-fp-navy-900 transition-colors px-2"
                  >
                    Sign In
                  </button>
                  {showAddressCta && <button
                    onClick={() => {
                      handleNavClick('home');
                      setTimeout(() => {
                        document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' });
                        document.getElementById('address-input')?.focus();
                      }, 100);
                    }}
                    className="bg-fp-teal-700 hover:bg-fp-navy-900 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fp-teal-700 font-inter font-semibold text-[14px] px-5 h-10 transition-colors flex items-center gap-1.5"
                  >
                    Check My Property
                    <ArrowRight className="h-4 w-4 text-white/80" aria-hidden />
                  </button>}
                </div>
              )}
            </div>
            )}

            {/* Tablet Navigation (1024px - 1279px) */}
            {navViewport === "tablet" && (
            <div className="flex items-center gap-4">
              {NAV_LINKS.map((link) => {
                // Direct-route links (e.g. CRE Pro → /commercial)
                if ('href' in link && link.href) {
                  return (
                    <Link
                      key={link.id}
                      to={link.href}
                      aria-current={activePage === link.id ? "page" : undefined}
                      className={clsx(
                        "font-inter font-medium text-[14px] transition-colors duration-150",
                        activePage === link.id ? "text-fp-emerald-600" : "text-fp-slate-700 hover:text-fp-slate-500"
                      )}
                    >
                      {link.label}
                    </Link>
                  );
                }

                if (link.id === 'how-it-works') {
                  return (
                    <div
                      key={link.id}
                      className="relative"
                      onMouseEnter={handleHowItWorksEnter}
                      onMouseLeave={handleHowItWorksLeave}
                    >
                      <button
                        onClick={() => setIsHowItWorksOpen((v) => !v)}
                        aria-expanded={isHowItWorksOpen}
                        aria-controls="fairpath-how-it-works-menu-tablet"
                        aria-haspopup="true"
                        className={clsx(
                          "font-inter font-medium text-[14px] transition-colors duration-150 flex items-center gap-1",
                          activePage === link.id ? "text-fp-emerald-600" : "text-fp-slate-700 hover:text-fp-slate-500"
                        )}
                      >
                        {link.label}
                        <ChevronDown className={clsx(
                          "w-3 h-3 transition-transform duration-200",
                          isHowItWorksOpen && "rotate-180"
                        )} />
                      </button>

                      <AnimatePresence>
                        {isHowItWorksOpen && (
                          <motion.div
                            id="fairpath-how-it-works-menu-tablet"
                            role="region"
                            aria-label="How FairPath works"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.18 }}
                            className="absolute right-0 top-full mt-3 w-[340px] bg-white rounded-xl border border-fp-slate-200 shadow-lg z-50 overflow-hidden"
                          >
                            <div className="p-4 space-y-3.5">
                              {HOW_IT_WORKS_STEPS.map(({ step, title, description, icon: Icon }) => (
                                <div key={step} className="flex items-start gap-3">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-fp-emerald-500/10 shrink-0">
                                    <Icon className="w-4 h-4 text-fp-emerald-500" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-inter font-semibold text-[13px] text-fp-navy-900 leading-snug">
                                      Step {step}: {title}
                                    </p>
                                    <p className="font-inter text-[12px] text-fp-slate-500 leading-relaxed mt-0.5">
                                      {description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-fp-slate-100 px-4 py-3">
                              <button
                                onClick={() => {
                                  setIsHowItWorksOpen(false);
                                  handleNavClick('home');
                                  setTimeout(() => {
                                    document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' });
                                  }, 100);
                                }}
                                className="w-full bg-fp-teal-700 hover:bg-fp-navy-900 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fp-teal-700 font-inter font-semibold text-[13px] rounded-lg h-9 transition-colors"
                              >
                                Get Started — Enter Your Address
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <button
                    key={link.id}
                    aria-current={activePage === link.id ? "page" : undefined}
                    onClick={() => handleNavClick(link.id)}
                    className={clsx(
                      "font-inter font-medium text-[14px] transition-colors duration-150",
                      activePage === link.id ? "text-fp-emerald-600" : "text-fp-slate-700 hover:text-fp-slate-500"
                    )}
                  >
                    {link.label}
                  </button>
                );
              })}
              {isAuthenticated ? (
                /* Tablet: open mobile slide-out (which has all user menu items)
                   instead of toggling the desktop-only dropdown. */
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="flex items-center gap-1.5 px-2.5 h-9 rounded-lg border border-fp-slate-200 hover:border-fp-slate-300 hover:bg-fp-slate-50 transition-colors"
                >
                  <div className="w-6 h-6 rounded-full bg-fp-emerald-500 flex items-center justify-center">
                    <span className="text-white text-[10px] font-semibold">{initials}</span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-fp-slate-500" />
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openAuth('login')}
                    className="font-inter text-[13px] text-fp-slate-500 hover:text-fp-navy-900 transition-colors px-2"
                  >
                    Sign In
                  </button>
                  {showAddressCta && <button
                    onClick={() => {
                      handleNavClick('home');
                      setTimeout(() => {
                        document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' });
                        document.getElementById('address-input')?.focus();
                      }, 100);
                    }}
                    className="bg-fp-teal-700 hover:bg-fp-navy-900 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fp-teal-700 font-inter font-semibold text-[13px] rounded-lg px-3.5 h-9 transition-colors flex items-center gap-1"
                  >
                    Check My Property
                    <ArrowRight className="h-4 w-4 text-white/80" aria-hidden />
                  </button>}
                </div>
              )}
            </div>
            )}

            {/* Mobile Hamburger */}
            {navViewport === "mobile" && (
            <div className="flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="text-fp-slate-700 p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center" // F-006 mobile pass: hamburger 44px touch target
                aria-label="Open navigation menu"
                aria-expanded={isMobileMenuOpen}
                aria-controls="fairpath-mobile-menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 bg-fp-navy-900/50 z-[60]"
              aria-hidden="true"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Slide-in Menu */}
            <motion.div
              id="fairpath-mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-label="FairPath navigation"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              className="fixed inset-y-0 right-0 w-full max-w-sm bg-white z-[70] shadow-xl flex flex-col"
            >
              {/* Header */}
              <div className="h-14 flex items-center justify-between px-4 border-b border-slate-100">
                <span className="font-inter font-bold text-[20px] text-fp-navy-900">
                  Menu
                </span>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-fp-slate-700 p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center" // F-006 mobile pass: close btn 44px touch target
                  aria-label="Close navigation menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* User info (if logged in) */}
              {isAuthenticated && (
                <div className="px-4 py-4 border-b border-slate-100 bg-fp-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-fp-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-semibold">{initials}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-inter font-semibold text-[16px] text-fp-navy-900 truncate">
                        {displayName}
                      </p>
                      <p className="font-inter text-[13px] text-fp-slate-500 truncate">
                        {displayEmail}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <div className="flex flex-col">
                  {NAV_LINKS.map((link) => {
                    // Direct-route links (e.g. CRE Pro → /commercial)
                    if ('href' in link && link.href) {
                      return (
                        <Link
                          key={link.id}
                          to={link.href}
                          aria-current={activePage === link.id ? "page" : undefined}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={clsx(
                            "text-left font-inter font-semibold text-[20px] h-14 flex items-center w-full border-b border-slate-50",
                            activePage === link.id ? "text-fp-emerald-600" : "text-fp-navy-900"
                          )}
                        >
                          {link.label}
                        </Link>
                      );
                    }

                    // "How It Works" expands inline on mobile
                    if (link.id === 'how-it-works') {
                      return (
                        <div key={link.id}>
                          <button
                            onClick={() => setIsHowItWorksMobileOpen((v) => !v)}
                            className={clsx(
                              "text-left font-inter font-semibold text-[20px] h-14 flex items-center justify-between w-full border-b border-slate-50",
                              activePage === link.id ? "text-fp-emerald-600" : "text-fp-navy-900"
                            )}
                          >
                            {link.label}
                            <ChevronDown className={clsx(
                              "w-5 h-5 text-fp-slate-400 transition-transform duration-200",
                              isHowItWorksMobileOpen && "rotate-180"
                            )} />
                          </button>
                          <AnimatePresence>
                            {isHowItWorksMobileOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="py-3 pl-2 pr-1 space-y-4">
                                  {HOW_IT_WORKS_STEPS.map(({ step, title, description, icon: Icon }) => (
                                    <div key={step} className="flex items-start gap-3">
                                      <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-fp-emerald-500/10 shrink-0">
                                        <Icon className="w-[18px] h-[18px] text-fp-emerald-500" />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-inter font-semibold text-[15px] text-fp-navy-900 leading-snug">
                                          Step {step}: {title}
                                        </p>
                                        <p className="font-inter text-[13px] text-fp-slate-500 leading-relaxed mt-0.5">
                                          {description}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => {
                                      setIsMobileMenuOpen(false);
                                      setIsHowItWorksMobileOpen(false);
                                      handleNavClick('home');
                                      setTimeout(() => {
                                        document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' });
                                      }, 100);
                                    }}
                                    className="w-full bg-fp-teal-700 hover:bg-fp-navy-900 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fp-teal-700 font-inter font-semibold text-[14px] rounded-lg h-10 transition-colors mt-1"
                                  >
                                    Get Started — Enter Your Address
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={link.id}
                        aria-current={activePage === link.id ? "page" : undefined}
                        onClick={() => handleNavClick(link.id)}
                        className={clsx(
                          "text-left font-inter font-semibold text-[20px] h-14 flex items-center w-full border-b border-slate-50",
                          activePage === link.id ? "text-fp-emerald-600" : "text-fp-navy-900"
                        )}
                      >
                        {link.label}
                      </button>
                    );
                  })}

                  {/* Auth-specific mobile links */}
                  {isAuthenticated && (
                    <>
                      <div className="h-3" />
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/appeal-hq'); }}
                        className="text-left font-inter font-semibold text-[18px] h-12 flex items-center gap-3 w-full text-fp-navy-900"
                      >
                        <Scale className="w-5 h-5 text-fp-slate-500" />
                        My Appeals
                      </button>
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/appeal-hq'); }}
                        className="text-left font-inter font-semibold text-[18px] h-12 flex items-center gap-3 w-full text-fp-navy-900"
                      >
                        <LayoutDashboard className="w-5 h-5 text-fp-slate-500" />
                        My Dashboard
                      </button>
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/account/settings'); }}
                        className="text-left font-inter font-semibold text-[18px] h-12 flex items-center gap-3 w-full text-fp-navy-900"
                      >
                        <Settings className="w-5 h-5 text-fp-slate-500" />
                        Account Settings
                      </button>
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/account/billing'); }}
                        className="text-left font-inter font-semibold text-[18px] h-12 flex items-center gap-3 w-full text-fp-navy-900"
                      >
                        <CreditCard className="w-5 h-5 text-fp-slate-500" />
                        Billing
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Footer CTA */}
              <div className="p-4 border-t border-slate-100">
                {isAuthenticated ? (
                  <button
                    onClick={handleSignOut}
                    className="w-full border border-fp-slate-300 text-fp-slate-700 hover:bg-fp-slate-50 font-inter font-semibold text-[16px] rounded-lg h-12 transition-colors flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                ) : (
                  <div className="flex flex-col gap-3">
                    {showAddressCta && <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleNavClick('home');
                        setTimeout(() => {
                          document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' });
                          document.getElementById('address-input')?.focus();
                        }, 100);
                      }}
                      className="w-full bg-fp-teal-700 hover:bg-fp-navy-900 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fp-teal-700 font-inter font-semibold text-[16px] rounded-lg h-12 transition-colors flex items-center justify-center gap-2"
                    >
                      Check My Property <ArrowRight className="h-4 w-4 text-white/80" aria-hidden />
                    </button>}
                    <button
                      onClick={() => { setIsMobileMenuOpen(false); openAuth('login'); }}
                      className="w-full border border-fp-slate-300 text-fp-navy-900 hover:bg-fp-slate-50 font-inter font-semibold text-[16px] rounded-lg h-12 transition-colors flex items-center justify-center"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Auth Modal — triggered by nav Sign In / Get Started buttons */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
        oauthReturnUrl="/appeal-hq"
        onSuccess={() => {
          setShowAuthModal(false);
          navigate('/appeal-hq');
        }}
      />
    </>
  );
}
